import React from 'react'

import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { EMAIL_ADDRESSES } from '@/lib/constants'
import { buildUrl } from '@/lib/helpers/helperFunctions'
import authService from '@/lib/services/authService'
import errorTypeService from '@/lib/services/errorTypeService'

interface Props {
  error?: unknown
}

const ExistingUserEmailRegistrationErrorMessage: React.FC<Props> = ({
  error,
}) => {
  // The backend embeds the conflicting address in brackets in the error
  // message - the only place the frontend can learn it, as session bootstrap
  // failed.
  const conflictingEmail = errorTypeService.isApiError(error)
    ? (error.response?.data['hydra:description']?.match(
        /\(([^\s()]+@[^\s()]+)\)/,
      )?.[1] ?? null)
    : null

  const contactSupportUrl = buildUrl(`mailto:${EMAIL_ADDRESSES.rqSupport}`, {
    searchParams: {
      subject: 'Unable to sign up - an account with this email already exists',
      body: `Hello,\n\nI tried to sign up to RQ with the email address ${conflictingEmail ?? '[your email address]'} but was told an account with this email address already exists on the platform.\n\n`,
    },
  })

  return (
    <>
      <Heading size={2}>This email address is already registered</Heading>

      <p className="text-md mb-4">
        An account with this email address
        {conflictingEmail && <> ({conflictingEmail})</>} already exists on the
        platform. Please contact support to regain access to your account, or
        sign up with a different email address.
      </p>

      <div className="flex items-center gap-2">
        <Button variant="primary" onClick={() => authService.logout()}>
          Use a different account
        </Button>

        <Button variant="outline-secondary" asChild>
          <a href={contactSupportUrl}>Contact support</a>
        </Button>
      </div>
    </>
  )
}

export default ExistingUserEmailRegistrationErrorMessage
